import React from 'react';

import DataConnector from 'connect/DataConnector';
import {LabelTemplate} from 'constants/SampleConstants';
import * as LabelPrintingActions from 'label-printing/LabelPrintingActions';
import StorableStore from 'samples/StorableStore';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface EnhancedProps {
  labelTemplates: Array<LabelTemplate> | null;
}

export interface LabelTemplateSelectorProps {
  onChange: (labelTemplate: LabelTemplate) => void;
  labelTemplateId: string | null;
  registryId: string;
}

class LabelTemplateSelectorConnector extends DataConnector<LabelTemplateSelectorProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [StorableStore];

  enhanceProps(): EnhancedProps {
    return {labelTemplates: this.storableStore().getLabelTemplates(this.props.registryId)};
  }
}

export class LabelTemplateSelector extends React.Component<LabelTemplateSelectorProps & EnhancedProps> {
  override componentDidMount(): void {
    LabelPrintingActions.fetchLabelTemplates();
    this.changeInitialLabelTemplate();
  }

  override componentDidUpdate(prevProps: LabelTemplateSelectorProps & EnhancedProps, prevState: {}): void {
    if (!prevProps.labelTemplates) {
      this.changeInitialLabelTemplate();
    }
  }

  private changeInitialLabelTemplate(): void {
    // Trigger onChange with initial labelTemplate if initial labelTemplateId is provided
    if (this.props.labelTemplates && this.props.labelTemplateId) {
      this.props.onChange(this.props.labelTemplates.find(({id}) => id === this.props.labelTemplateId)!);
    }
  }

  private getLabelTemplateDropdownOptions(): Array<OptionData> {
    if (!this.props.labelTemplates) {
      return [];
    }
    if (this.props.labelTemplates.length === 0) {
      return [{name: 'No label templates found', value: null}];
    }
    return this.props.labelTemplates.map((labelTemplate) => ({
      name: labelTemplate.name,
      value: labelTemplate.id,
    }));
  }

  private handleOnSelect = (value: OptionValue): void => {
    if (this.props.labelTemplates && value) {
      this.props.onChange(this.props.labelTemplates.find(({id}) => id === value)!);
    }
  };

  override render(): JSX.Element {
    const selectedLabelTemplate = this.props.labelTemplates?.find(
      ({id}) => id === this.props.labelTemplateId
    );
    return (
      <SelectDropdown
        displayName={selectedLabelTemplate ? selectedLabelTemplate.name : 'Select a label template...'}
        disabled={!this.props.labelTemplates}
        menu={
          <SearchableMenu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            onSelect={this.handleOnSelect}
            optionDatas={this.getLabelTemplateDropdownOptions()}
          />
        }
      />
    );
  }
}

export default LabelTemplateSelectorConnector.connect(LabelTemplateSelector);
